import { useContext, useEffect } from "react";
import mixpanel from "mixpanel-browser";
import { useAuth } from "./useAuth";
import { LanguageContext } from "./LanguageContext";

interface UserInfo {
  unicityId?: string;
  countryCode?: string;
  joinDate?: string;
  highestRank?: string;
  partnersCount?: number;
  totalOv?: number;
  membersCount?: number;
}

interface AuthContextType {
  userInfo: UserInfo | null;
}

interface LanguageContextType {
  currentLanguage: string;
}

// $last_seen is refreshed at most once per this window while a session is
// active — one /engage per window, negligible vs the pre-GO-2864
// 60–120-POST-per-login burst.
const SESSION_REFRESH_MS = 30 * 60 * 1000; // 30 minutes
// How often the heartbeat *checks* whether a refresh is due. Checking is free;
// the actual write is gated by SESSION_REFRESH_MS, so a shorter check interval
// just means we write promptly once the window elapses (no extra /engage).
const SESSION_CHECK_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Mounted exactly once at the top of the provider tree.
 *
 * Was: `mixpanel.identify` + `mixpanel.people.set` ran inside every
 * `useMixPanel()` consumer's useEffect. With ~56 consumers mounted on the
 * initial post-login dashboard render (including per-row data-grid cells),
 * `unicityId` becoming available fired 60–120 simultaneous /engage POSTs
 * setting the same 7 fields on the same user.
 *
 * Now: identify + the full profile set fire from this single instance — one
 * /engage round-trip per login transition, regardless of how many components
 * read `logEvent` via `useMixPanel`.
 *
 * Plus (GO-2864 QA / PM follow-up): `$last_seen` is refreshed PER SESSION, not
 * only at login. Distributors with a long-lived auth token can go a month+
 * without re-authenticating (silent login), so a once-per-login write left them
 * looking inactive in Mixpanel even while using the app daily. A throttled
 * heartbeat (while the tab is visible) + a refresh on tab refocus touch
 * `$last_seen` at most once per SESSION_REFRESH_MS — still one mount point, so
 * NOT the per-navigation flood we removed.
 */
export const MixPanelIdentitySync = () => {
  const { userInfo } = useAuth() as unknown as AuthContextType;
  const { currentLanguage } = useContext(
    LanguageContext
  ) as unknown as LanguageContextType;

  const unicityId = userInfo?.unicityId;

  useEffect(() => {
    if (!unicityId || !userInfo) return;

    // Session start (app load / login transition): bind identity + write the
    // full profile. identify() persists the distinct_id, so the heartbeat can
    // refresh the profile afterwards without re-identifying (no extra $identify).
    mixpanel.identify(unicityId);
    mixpanel.people.set({
      $country_code: userInfo.countryCode,
      $created: userInfo.joinDate,
      highestRank: userInfo.highestRank,
      totalPartners: userInfo.partnersCount,
      totalOv: userInfo.totalOv,
      totalMembers: userInfo.membersCount,
      language: currentLanguage,
    });

    // Per-session $last_seen refresh (see component doc). Throttled + visibility
    // gated so it stays at most one /engage per SESSION_REFRESH_MS per session.
    let lastTouch = Date.now();
    const touchLastSeen = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (Date.now() - lastTouch < SESSION_REFRESH_MS) return;
      mixpanel.people.set({ $last_seen: new Date().toISOString() });
      lastTouch = Date.now();
    };
    const heartbeat = window.setInterval(touchLastSeen, SESSION_CHECK_MS);
    document.addEventListener("visibilitychange", touchLastSeen);

    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", touchLastSeen);
    };
    // Deps = [unicityId] only: re-run on identity transitions, not on every
    // render / language / stat refresh — re-firing the full set on those is what
    // produced the original /engage flood. userInfo + currentLanguage are read
    // at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unicityId]);

  return null;
};
